/**
 * ConsulenteSidebar — sidebar for the Consulente portal.
 * Five flat navigation items (no accordion), logout, and optional admin link.
 */

import { Link, useLocation } from 'react-router-dom'
import {
  X,
  Users,
  LayoutGrid,
  LayoutDashboard,
  Target,
  FileBarChart,
  Settings,
  Shield,
  LogOut,
  ClipboardList,
  Scale,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'

interface ConsulenteSidebarProps {
  isOpen: boolean
  onClose: () => void
}

interface NavItem {
  label: string
  path: string
  icon: React.ElementType
  /** When true the link leaves the portal (no startsWith matching) */
  exact?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Command Center', path: '/consulente', icon: LayoutDashboard, exact: true },
  { label: 'Clienti', path: '/consulente/clienti', icon: Users },
  { label: 'Panoramica', path: '/consulente/panoramica', icon: LayoutGrid },
  { label: 'Task', path: '/consulente/task', icon: ClipboardList },
  { label: 'Opportunità', path: '/consulente/bandi', icon: Target },
  { label: 'Report', path: '/consulente/report', icon: FileBarChart },
  { label: 'Brevetti', path: '/consulente/brevetti', icon: Scale },
  { label: 'Impostazioni', path: '/profile', icon: Settings, exact: true },
]

export function ConsulenteSidebar({ isOpen, onClose }: ConsulenteSidebarProps) {
  const location = useLocation()
  const { user, logout } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'

  const isActive = (item: NavItem) => {
    if (item.exact) return location.pathname === item.path
    return location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  }

  const navLinkClass = (item: NavItem) =>
    cn(
      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
      isActive(item)
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
    )

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar panel */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-screen w-64 border-r bg-background transition-transform lg:translate-x-0 lg:static',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Mobile header */}
          <div className="flex h-16 items-center justify-between border-b px-4 lg:hidden">
            <span className="font-semibold">Menu</span>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Desktop brand */}
          <div className="hidden h-16 items-center border-b px-4 lg:flex">
            <Link to="/consulente" className="flex items-center gap-2" onClick={onClose}>
              <span className="text-lg font-bold tracking-tight text-primary">
                Research<span className="text-foreground">Constellation</span>
              </span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 overflow-y-auto p-4">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={navLinkClass(item)}
                  onClick={onClose}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span>{item.label}</span>
                </Link>
              )
            })}

            <div className="pt-4">
              <hr className="border-border" />
            </div>

            {/* Admin link — only for admin users */}
            {isAdmin &&
              (() => {
                const adminItem: NavItem = {
                  label: 'Amministrazione',
                  path: '/admin',
                  icon: Shield,
                }
                return (
                  <Link to="/admin" className={navLinkClass(adminItem)} onClick={onClose}>
                    <Shield className="h-5 w-5 shrink-0" />
                    <span>Amministrazione</span>
                  </Link>
                )
              })()}
          </nav>

          {/* Logout */}
          <div className="border-t p-4">
            <button
              onClick={() => {
                onClose()
                logout()
              }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              <span>Esci</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}
